/**
 * Game store for managing game state and session information.
 * Stores complete game state that syncs with server updates.
 */

import { create } from 'zustand'
import i18next from 'i18next'
import { GameState, GameStateDelta, Phase, Player, PlayerDelta } from '@/types/game'
import type { QuestionPanelDefinition } from '@/types/actions'
import type { WhisperWord } from '@/stores/audioStore'
import { SUPPORTED_LOCALES, detectSupportedLocale } from '@/types/auth'
import type { Locale } from '@/types/auth'
import { getPlayerState, getTVState } from '@/services/api'
import type { PlanOption } from '@/components/high/billing/PaywallModal'

/**
 * Where a pending redirect should navigate: back to the session lobby (game ended)
 * or to the home screen (session no longer exists / is closed).
 */
export type RedirectTarget = 'lobby' | 'home'

/** Width of the TV sidebar container, chosen by the game mode via SDK. */
export type TVSidebarWidth = 'narrow' | 'standard' | 'wide'

const TV_SIDEBAR_WIDTHS: readonly TVSidebarWidth[] = ['narrow', 'standard', 'wide']

type TVRegionName = 'header' | 'sidebar' | 'main' | 'footer' | 'fullscreen'

/** Validate a sidebar width from the backend; anything outside the contract is a bug. */
export function parseTVSidebarWidth(raw: unknown): TVSidebarWidth {
  if (typeof raw === 'string' && (TV_SIDEBAR_WIDTHS as readonly string[]).includes(raw)) {
    return raw as TVSidebarWidth
  }
  throw new Error(i18next.t('tv.invalidState', { field: `tv_sidebar_width=${String(raw)}` }))
}

/** Read a TV region's HTML; a missing key means the mode never displayed that region. */
function readTVRegionHtml(publicState: Record<string, unknown>, region: TVRegionName): string {
  const raw = publicState[`tv_${region}`]
  if (raw === undefined) return ''
  if (typeof raw !== 'string') {
    throw new Error(i18next.t('tv.invalidState', { field: `tv_${region}` }))
  }
  return raw
}

/** Read a TV region's unsafe flag; a missing key means the mode never displayed that region. */
function readTVRegionUnsafe(publicState: Record<string, unknown>, region: TVRegionName): boolean {
  const raw = publicState[`tv_${region}_unsafe`]
  if (raw === undefined) return false
  if (typeof raw !== 'boolean') {
    throw new Error(i18next.t('tv.invalidState', { field: `tv_${region}_unsafe` }))
  }
  return raw
}

/**
 * Parse raw pending_actions from REST into QuestionPanelDefinition.
 * Returns null if panel_type is unknown or data is invalid.
 */
function parseQuestionPanel(raw: unknown): QuestionPanelDefinition | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  const panelType = r['panel_type']
  if (panelType === 'action') return raw as QuestionPanelDefinition
  if (panelType === 'multiselect') return raw as QuestionPanelDefinition
  if (panelType === 'form') return raw as QuestionPanelDefinition
  if (panelType === 'grid') return raw as QuestionPanelDefinition
  console.error('[gameStore] Unknown panel_type in pending_actions:', panelType)
  return null
}

// Paywall data from billing:paywall_required WS event
export interface PaywallData {
  message: string
  cutoff_label: string | null
  plans: PlanOption[]
}

interface GameStoreState extends Partial<GameState> {
  // State
  sessionId: string | null
  sessionCode: string | null
  phase: Phase
  players: Player[]
  version: number

  // BINGO State
  bingoCard: number[][] | null
  calledNumbers: number[]
  lastCalledNumber: number | null
  bingoWinner: string | null

  // Questions/Actions (Story 5.4)
  currentQuestion: {
    requestId: string
    panel: QuestionPanelDefinition
    receivedAt: number
  } | null

  // PHASE 8: TV Display Regions (HTML content from SDK)
  tvRegions: {
    header: string
    sidebar: string
    main: string
    footer: string
    fullscreen: string
  }

  // PHASE 8: TV Display Regions - Unsafe HTML flags (per-region)
  tvRegionsUnsafe: {
    header: boolean
    sidebar: boolean
    main: boolean
    footer: boolean
    fullscreen: boolean
  }

  // TV sidebar container width (set by the mode alongside sidebar content)
  tvSidebarWidth: TVSidebarWidth

  // Title of the current game mode in the session locale (TV HUD)
  gameModeTitle: string

  // PHASE 8: Phone Info Area (private display per player)
  phoneInfo: string

  // PHASE 8: Phone Info - Unsafe HTML flag
  phoneInfoUnsafe: boolean

  // Loading Overlay
  loadingOverlay: {
    visible: boolean
    messages: string[]
  } | null

  // Redirect management (for game:finished / session:closed)
  pendingRedirectAt: number | null
  pendingRedirectDelay: number | null
  pendingRedirectTarget: RedirectTarget
  /** Session the pending redirect belongs to; guards against firing it in a later session. */
  pendingRedirectSessionId: string | null

  // Actions
  applyDelta: (delta: GameStateDelta) => void
  fetchState: (sessionId: string, playerId: string) => Promise<void>
  fetchTVState: (sessionId: string, playerId: string) => Promise<void>
  reset: () => void
  resetGameState: () => void
  setBingoCard: (card: number[][]) => void
  setCalledNumbers: (numbers: number[]) => void
  setLastCalledNumber: (num: number | null) => void
  addCalledNumber: (num: number) => void
  setBingoWinner: (playerId: string) => void
  setCurrentQuestion: (requestId: string, panel: QuestionPanelDefinition) => void
  clearCurrentQuestion: () => void

  // PHASE 8: Display Actions
  updateTVRegion: (region: 'header' | 'sidebar' | 'main' | 'footer' | 'fullscreen', html: string, unsafe: boolean) => void
  setTVSidebarWidth: (width: TVSidebarWidth) => void
  updatePhoneInfo: (html: string, unsafe: boolean) => void

  // Loading Overlay Actions
  setLoadingOverlay: (data: { visible: boolean; messages: string[] }) => void
  clearLoadingOverlay: () => void

  // Redirect Actions
  setPendingRedirect: (delayMs: number, target?: RedirectTarget, sessionId?: string | null) => void
  clearPendingRedirect: () => void

  // Paywall slice (billing:paywall_required)
  paywallData: PaywallData | null
  setPaywallData: (data: PaywallData) => void
  clearPaywallData: () => void

  // Display player (for TTS emit guard — proxy via variant since display_player_id not in public_state)
  displayPlayerId: string | null
  setDisplayPlayerId: (id: string | null) => void

  // Selectors (computed)
  getPlayer: (playerId: string) => Player | undefined
  getAlivePlayers: () => Player[]
}

const initialState = {
  sessionId: null,
  sessionCode: null,
  phase: 'lobby' as Phase,
  players: [],
  current_round: 0,
  timer_seconds: null,
  timer_started_at: null,
  winner: null,
  pending_question: null,
  narrator_text: '',
  display_data: {},
  version: 0,
  last_updated: new Date().toISOString(),
  called_numbers: [],
  bingo_winner: null,
  // BINGO state
  bingoCard: null,
  calledNumbers: [],
  lastCalledNumber: null,
  bingoWinner: null,
  // Questions/Actions (Story 5.4)
  currentQuestion: null,
  // PHASE 8: TV Display Regions
  tvRegions: {
    header: '',
    sidebar: '',
    main: '',
    footer: '',
    fullscreen: '',
  },
  // PHASE 8: TV Display Regions - Unsafe flags
  tvRegionsUnsafe: {
    header: false,
    sidebar: false,
    main: false,
    footer: false,
    fullscreen: false,
  },
  tvSidebarWidth: 'standard' as TVSidebarWidth,
  gameModeTitle: '',
  // PHASE 8: Phone Info
  phoneInfo: '',
  // PHASE 8: Phone Info - Unsafe flag
  phoneInfoUnsafe: false,
  // Loading Overlay
  loadingOverlay: null,
  // Redirect management
  pendingRedirectAt: null,
  pendingRedirectDelay: null,
  pendingRedirectTarget: 'lobby' as RedirectTarget,
  pendingRedirectSessionId: null,
  // Paywall slice
  paywallData: null,
  // Display player
  displayPlayerId: null,
}

export const useGameStore = create<GameStoreState>((set, get) => ({
  ...initialState,

  applyDelta: (delta) => {
    set((state) => {
      // Version check - CRITICAL for sync integrity
      if (delta.version !== undefined && delta.version !== state.version + 1) {
        console.warn(
          `State version mismatch: expected ${state.version + 1}, got ${delta.version}`
        )
        // Trigger full state fetch - see useGameSync hook
        // Don't apply stale delta
        return state
      }

      // Start with immutable merge
      const newState = { ...state, ...delta }

      // Handle GRANULAR players delta
      // Players delta is array of partial updates, not full player objects
      if (delta.players && Array.isArray(delta.players)) {
        const playersById = new Map(state.players.map((p) => [p.id, p]))

        for (const change of delta.players as PlayerDelta[]) {
          if (change._removed) {
            // Player was removed
            playersById.delete(change.id)
          } else if (playersById.has(change.id)) {
            // Merge changes into existing player (granular update)
            const existing = playersById.get(change.id)!
            playersById.set(change.id, { ...existing, ...change } as Player)
          } else {
            // New player - change should contain all fields
            playersById.set(change.id, change as Player)
          }
        }

        newState.players = Array.from(playersById.values())
      }

      return newState
    })

    // TV locale revert: when game ends, revert to navigator-detected locale
    // TV is a guest client (no authStore.user), so revert like a guest
    // Must run outside set() callback (synchronous constraint)
    if (delta.phase === 'game_over') {
      const detected = detectSupportedLocale(navigator.language)
      i18next.changeLanguage(detected)
      void import('@/stores/playerStore').then(({ usePlayerStore }) => {
        usePlayerStore.getState().setSessionLocale(null)
      })
    }
  },

  fetchState: async (sessionId, playerId) => {
    const response = await getPlayerState(sessionId, playerId)
    const data = response.data as unknown as {
      public_state: GameState & Record<string, unknown>;
      private_state?: { phone_info?: string; phone_info_unsafe?: boolean };
      pending_actions?: QuestionPanelDefinition & { request_id?: string };
      version?: number;
      timestamp?: string;
      active_tts?: { stream_id: string; url: string; whisper_url?: string; cache_key?: string; whisper_words?: WhisperWord[] };
    }

    // Extract phone_info from private_state if available
    const privateState = data.private_state || {}
    const phoneInfo = privateState.phone_info || ''
    const phoneInfoUnsafe = privateState.phone_info_unsafe || false

    // Extract pending_actions (question panel)
    const pendingActionsRaw = data.pending_actions
    let currentQuestion = null
    if (pendingActionsRaw) {
      const parsedPanel = parseQuestionPanel(pendingActionsRaw)
      if (parsedPanel) {
        currentQuestion = {
          requestId: pendingActionsRaw.request_id || pendingActionsRaw.id || '',
          panel: parsedPanel,
          receivedAt: Date.now(),
        }
      }
    }

    // Extract loading overlay from public_state
    const loadingOverlay = (data.public_state?.loading_overlay as { visible: boolean; messages: string[] } | undefined) || null

    set({
      sessionId: data.public_state?.session_id || sessionId,
      sessionCode: data.public_state?.session_code || '',
      phase: (data.public_state?.phase as Phase) || 'lobby',
      players: (data.public_state?.players as Player[]) || [],
      current_round: data.public_state?.current_round || 0,
      timer_seconds: data.public_state?.timer_seconds || null,
      timer_started_at: data.public_state?.timer_started_at || null,
      winner: data.public_state?.winner || null,
      narrator_text: data.public_state?.narrator_text || '',
      display_data: data.public_state?.display_data || {},
      version: data.version || 0,
      last_updated: data.timestamp || new Date().toISOString(),
      called_numbers: data.public_state?.called_numbers || [],
      bingo_winner: data.public_state?.bingo_winner || null,
      // PHASE 8: Phone info from private_state
      phoneInfo,
      phoneInfoUnsafe,
      // PHASE 8: Current question from pending_actions
      currentQuestion,
      pending_question: null,
      // Loading overlay from public_state
      loadingOverlay,
    })

    // REST locale sync: apply session locale if present in public_state
    const rawLocalePhone = data.public_state?.locale
    if (typeof rawLocalePhone === 'string' && SUPPORTED_LOCALES.includes(rawLocalePhone as Locale)) {
      const sessionLocale = rawLocalePhone as Locale
      i18next.changeLanguage(sessionLocale)
      const { usePlayerStore } = await import('@/stores/playerStore')
      usePlayerStore.getState().setSessionLocale(sessionLocale)
    }

    // TTS reconnect resilience: Resume active TTS if present (phone view)
    // This handles the race condition where tts:play event is emitted before
    // frontend listeners are registered (e.g., on game start)
    if (data.active_tts) {
      const { handleTtsPlay } = await import('@/services/socketEventRouter')
      handleTtsPlay(data.active_tts)
    }
  },

  fetchTVState: async (sessionId, playerId) => {
    const response = await getTVState(sessionId, playerId)
    const data = response.data as {
      public_state: Record<string, unknown>;
      game_mode_title: unknown;
      version?: number;
      timestamp?: string;
      active_tts?: { stream_id: string; url: string; whisper_url?: string; cache_key?: string; whisper_words?: WhisperWord[] };
    }

    if (typeof data.game_mode_title !== 'string') {
      throw new Error(i18next.t('tv.invalidState', { field: 'game_mode_title' }))
    }
    const gameModeTitle = data.game_mode_title

    // Extract TV regions from public_state (tv_main, tv_header, etc.)
    const publicState = data.public_state
    const tvRegions = {
      header: readTVRegionHtml(publicState, 'header'),
      sidebar: readTVRegionHtml(publicState, 'sidebar'),
      main: readTVRegionHtml(publicState, 'main'),
      footer: readTVRegionHtml(publicState, 'footer'),
      fullscreen: readTVRegionHtml(publicState, 'fullscreen'),
    }

    // Extract unsafe flags from public_state (tv_main_unsafe, etc.)
    const tvRegionsUnsafe = {
      header: readTVRegionUnsafe(publicState, 'header'),
      sidebar: readTVRegionUnsafe(publicState, 'sidebar'),
      main: readTVRegionUnsafe(publicState, 'main'),
      footer: readTVRegionUnsafe(publicState, 'footer'),
      fullscreen: readTVRegionUnsafe(publicState, 'fullscreen'),
    }

    // Missing key: the mode never displayed the sidebar, so the default width applies
    const tvSidebarWidth: TVSidebarWidth =
      publicState.tv_sidebar_width === undefined
        ? 'standard'
        : parseTVSidebarWidth(publicState.tv_sidebar_width)

    console.log('[fetchTVState] Loaded TV regions:', {
      main_length: tvRegions.main.length,
      main_unsafe: tvRegionsUnsafe.main,
      raw_unsafe: data.public_state?.tv_main_unsafe,
    })

    // Extract loading overlay from public_state
    const loadingOverlay = (data.public_state?.loading_overlay as { visible: boolean; messages: string[] } | undefined) || null

    // TV REST locale sync: validate and apply session locale from public_state
    const rawLocaleTV = data.public_state?.locale
    const tvLocale: Locale | null =
      typeof rawLocaleTV === 'string' && SUPPORTED_LOCALES.includes(rawLocaleTV as Locale)
        ? (rawLocaleTV as Locale)
        : null
    if (tvLocale !== null) {
      i18next.changeLanguage(tvLocale)
      const { usePlayerStore } = await import('@/stores/playerStore')
      usePlayerStore.getState().setSessionLocale(tvLocale)
    }

    // TV only gets public_state (no private_state or pending_actions)
    set({
      sessionId: data.public_state?.session_id as string || sessionId,
      sessionCode: data.public_state?.session_code as string || '',
      phase: (data.public_state?.phase as Phase) || 'lobby',
      players: (data.public_state?.players as Player[]) || [],
      current_round: data.public_state?.current_round as number || 0,
      timer_seconds: data.public_state?.timer_seconds as number | null || null,
      timer_started_at: data.public_state?.timer_started_at as string | null || null,
      winner: data.public_state?.winner as string | null || null,
      narrator_text: (data.public_state?.narrator_text as string) || '',
      display_data: (data.public_state?.display_data as Record<string, unknown>) || {},
      version: data.version || 0,
      last_updated: data.timestamp || new Date().toISOString(),
      called_numbers: (data.public_state?.called_numbers as number[]) || [],
      bingo_winner: (data.public_state?.bingo_winner as string | null) || null,
      // PHASE 8: TV regions from public_state
      tvRegions,
      tvRegionsUnsafe,
      tvSidebarWidth,
      gameModeTitle,
      // Loading overlay from public_state
      loadingOverlay,
    })

    // TTS reconnect resilience: Resume active TTS if present
    if (data.active_tts) {
      const { handleTtsPlay } = await import('@/services/socketEventRouter')
      handleTtsPlay(data.active_tts)
    }
  },

  reset: () => {
    set(initialState)
  },

  resetGameState: () => {
    // Reset game-specific state but preserve session identity and player list
    set((state) => ({
      phase: 'lobby' as Phase,
      version: 0,
      tvRegions: { header: '', sidebar: '', main: '', footer: '', fullscreen: '' },
      tvRegionsUnsafe: { header: false, sidebar: false, main: false, footer: false, fullscreen: false },
      tvSidebarWidth: 'standard' as TVSidebarWidth,
      gameModeTitle: '',
      phoneInfo: '',
      phoneInfoUnsafe: false,
      currentQuestion: null,
      loadingOverlay: null,
      pendingRedirectAt: null,
      pendingRedirectDelay: null,
      pendingRedirectTarget: 'lobby' as RedirectTarget,
      pendingRedirectSessionId: null,
      paywallData: null,
      // BINGO state (AC4: Play Again must not start with dirty state)
      bingoCard: null,
      calledNumbers: [],
      lastCalledNumber: null,
      bingoWinner: null,
      called_numbers: [],
      bingo_winner: null,
      // Other game-specific fields (prevent visual artifacts on Play Again)
      narrator_text: '',
      display_data: {},
      current_round: 0,
      winner: null,
      timer_seconds: null,
      timer_started_at: null,
      pending_question: null,
      // Preserve: state.sessionId, state.sessionCode, state.players
      sessionId: state.sessionId,
      sessionCode: state.sessionCode,
      players: state.players,
    }))
  },

  getPlayer: (playerId) => {
    return get().players.find((p) => p.id === playerId)
  },

  getAlivePlayers: () => {
    return get().players.filter((p) => p.is_alive)
  },

  setBingoCard: (card) => {
    set({ bingoCard: card })
  },

  setCalledNumbers: (numbers) => {
    set({ calledNumbers: numbers })
  },

  setLastCalledNumber: (num) => {
    set({ lastCalledNumber: num })
  },

  addCalledNumber: (num) => {
    set((state) => ({
      calledNumbers: [...state.calledNumbers, num],
      lastCalledNumber: num,
    }))
  },

  setBingoWinner: (playerId) => {
    set({ bingoWinner: playerId })
  },

  setCurrentQuestion: (requestId, panel) => {
    set({
      currentQuestion: {
        requestId,
        panel,
        receivedAt: Date.now(),
      },
    })
  },

  clearCurrentQuestion: () => {
    set({ currentQuestion: null })
  },

  // PHASE 8: Display actions
  updateTVRegion: (region, html, unsafe) => {
    set((state) => ({
      tvRegions: {
        ...state.tvRegions,
        [region]: html,
      },
      tvRegionsUnsafe: {
        ...state.tvRegionsUnsafe,
        [region]: unsafe,
      },
    }))
  },

  setTVSidebarWidth: (width) => {
    set({ tvSidebarWidth: width })
  },

  updatePhoneInfo: (html, unsafe) => {
    set({ phoneInfo: html, phoneInfoUnsafe: unsafe })
  },

  setLoadingOverlay: (data) => {
    set({ loadingOverlay: data })
  },

  clearLoadingOverlay: () => {
    set({ loadingOverlay: null })
  },

  setPendingRedirect: (delayMs, target = 'lobby', sessionId = null) => set({
    pendingRedirectAt: Date.now(),
    pendingRedirectDelay: delayMs,
    pendingRedirectTarget: target,
    pendingRedirectSessionId: sessionId,
  }),

  clearPendingRedirect: () => set({
    pendingRedirectAt: null,
    pendingRedirectDelay: null,
    pendingRedirectTarget: 'lobby',
    pendingRedirectSessionId: null,
  }),

  setPaywallData: (data) => set({ paywallData: data }),

  clearPaywallData: () => set({ paywallData: null }),

  setDisplayPlayerId: (id) => set({ displayPlayerId: id }),
}))
